import { useEffect, useState } from 'react';
import { useAuth } from '@/modules/auth/useAuth';
import { getSupabase } from '@/lib/supabase';

/**
 * Membership check against `public.hottr_moderators`.
 *
 * Re-verifies against the network on every mount — the previous version
 * trusted a sessionStorage cache as the source of truth, which produced
 * a stale-redirect bug when a user was promoted to moderator AFTER they
 * had already visited the site once (their tab held `false` and never
 * re-checked, so /admin/* redirected straight to /feed).
 *
 * We still keep an optimistic cached value to avoid a render flash, but
 * `loading: true` stays set until the network check completes — so the
 * `ProtectedRoute` gate waits rather than redirecting on cached `false`.
 */
const CACHE_KEY = 'hottr.is_moderator';

export interface ModeratorState {
  loading: boolean;
  isModerator: boolean;
}

const readCache = (): boolean | null => {
  if (typeof window === 'undefined') return null;
  const cached = window.sessionStorage.getItem(CACHE_KEY);
  if (cached === 'true') return true;
  if (cached === 'false') return false;
  return null;
};

export const useIsModerator = (): ModeratorState => {
  const { user, loading: authLoading } = useAuth();
  const [state, setState] = useState<ModeratorState>(() => ({
    loading: true,
    isModerator: readCache() ?? false,
  }));

  useEffect(() => {
    // While auth is still settling, keep our own loading state true so
    // ProtectedRoute does not redirect on a transient `user === null`.
    if (authLoading) {
      setState((prev) => ({ ...prev, loading: true }));
      return;
    }
    if (!user) {
      setState({ loading: false, isModerator: false });
      return;
    }
    const supabase = getSupabase();
    if (!supabase) {
      setState({ loading: false, isModerator: false });
      return;
    }
    // Mark loading at the start of the network probe so any earlier
    // transient `loading: false` is reset before the guard reads us.
    setState((prev) => ({ ...prev, loading: true }));
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('hottr_moderators')
        .select('user_id')
        .eq('user_id', user.id)
        .maybeSingle();
      if (cancelled) return;
      const isModerator = Boolean(data);
      window.sessionStorage.setItem(CACHE_KEY, isModerator ? 'true' : 'false');
      setState({ loading: false, isModerator });
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, authLoading]);

  return state;
};
